import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import ArrayFields from '../ArrayFields';
import FileUpload from '../FileUpload';
import { useStyles } from '../styles';
import MultiText from '../MultiText';
import CustomDateField from '../CustomDateField';
import { Autocomplete, Box, FormControlLabel, InputAdornment, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { EventFormJsonFields } from '../../../utils/interfaces';
import { useAppFormContext } from '../../../utils/formContext';
import KenTextLabel from '../../KenTextLabel';
import KenTextField from '../../KenTextField';
import KenInput from '../../KenInput';
import KenSelect from '../../KenSelect';
import { getCategoryPicklistValue, getEventsData } from '../../../../../Utils/apiService';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

const FieldsBuilder: React.FunctionComponent<{ fields: EventFormJsonFields }> = (props) => {
  const { fields } = props;
  const { pathname, search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const eventId = searchParams.get('eventId');
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { control, register, setError, setValue } = useFormContext();
  const {
    state: { options, range },
    dispatch,
  } = useAppFormContext();
  const [processedValue, setProcessedValue] = useState<any[]>([]);
  
  // Debug logging for time range fields
  if (fields.type === 'timeRange') {
    console.log('FieldsBuilder - Time range field detected:', {
      fieldName: fields.name,
      fieldType: fields.type,
      currentRange: range,
      rangeKeys: Object.keys(range),
      fieldRange: range[fields.name as any],
      fieldProps: fields,
      control: !!control,
      setValue: !!setValue
    });
  }
  
  // Watch the start time value for time range fields to properly handle disabled state
  const startTimeValue = useWatch({
    control,
    name: fields.type === 'timeRange' ? `${fields.name}.startTime` : '',
  });
  
  // Watch the end time value for time range fields to ensure proper synchronization
  const endTimeValue = useWatch({
    control,
    name: fields.type === 'timeRange' ? `${fields.name}.endTime` : '',
  });
  
  // Synchronize form state with local context for time range fields
  useEffect(() => {
    if (fields.type === 'timeRange') {
      // Try to find field range with current name first
      let fieldRange = range[fields.name as any] as any;
      
      // If not found, try with original field name (before dash)
      if (!fieldRange && fields.name && fields.name.includes('-')) {
        const originalFieldName = fields.name.split('-')[0];
        fieldRange = range[originalFieldName as any] as any;
      }
      
      console.log('FieldsBuilder - Sync useEffect (form to context):', {
        fieldName: fields.name,
        fieldRange,
        startTimeValue,
        endTimeValue,
        rangeKeys: Object.keys(range)
      });
      
      if (fieldRange) {
        // Update form state with current context values if they exist
        if (fieldRange.startTime && !startTimeValue) {
          console.log('FieldsBuilder - Setting start time in form:', fieldRange.startTime);
          setValue(`${fields.name}.startTime`, fieldRange.startTime);
        }
        if (fieldRange.endTime && !endTimeValue) {
          console.log('FieldsBuilder - Setting end time in form:', fieldRange.endTime);
          setValue(`${fields.name}.endTime`, fieldRange.endTime);
        }
      }
    }
  }, [fields.type, fields.name, range, startTimeValue, endTimeValue, setValue]);
  
  // Synchronize local context with form state changes
  useEffect(() => {
    if (fields.type === 'timeRange' && (startTimeValue || endTimeValue)) {
      // Try to find current range with current name first
      let currentRange = range[fields.name as any] as any;
      
      // If not found, try with original field name (before dash)
      if (!currentRange && fields.name && fields.name.includes('-')) {
        const originalFieldName = fields.name.split('-')[0];
        currentRange = range[originalFieldName as any] as any;
      }
      
      const newStartTime = startTimeValue || currentRange?.startTime || '';
      const newEndTime = endTimeValue || currentRange?.endTime || '';
      
      console.log('FieldsBuilder - Sync useEffect (context to form):', {
        fieldName: fields.name,
        currentRange,
        newStartTime,
        newEndTime,
        startTimeValue,
        endTimeValue,
        rangeKeys: Object.keys(range)
      });
      
      // Only update if values have actually changed
      if (newStartTime !== currentRange?.startTime || newEndTime !== currentRange?.endTime) {
        // Update both the current field name and the original field name in the context
        const updateData = {
          fieldName: fields.name,
          fileData: { startTime: newStartTime, endTime: newEndTime },
        };
        
        console.log('FieldsBuilder - Dispatching updateTimeRange:', updateData);
        
        dispatch({
          type: 'updateTimeRange',
          value: updateData,
        });
        
        // Also update the original field name if it's different
        if (fields.name && fields.name.includes('-')) {
          const originalFieldName = fields.name.split('-')[0];
          if (originalFieldName !== fields.name) {
            console.log('FieldsBuilder - Also updating original field name in useEffect:', originalFieldName);
            dispatch({
              type: 'updateTimeRange',
              value: {
                fieldName: originalFieldName,
                fileData: { startTime: newStartTime, endTime: newEndTime },
              },
            });
          }
        }
      }
    }
  }, [fields.type, fields.name, startTimeValue, endTimeValue, range, dispatch]);
  
  switch (fields.type) {
    case 'coverField':
      return <FileUpload field={{ ...fields }} />;
    case 'upload':
      return <FileUpload field={{ ...fields }} />;
    case 'arrayField':
      return <ArrayFields {...fields} control={control} />;
    case 'multiText':
      return <MultiText {...fields} />;
    case 'timeRange':
      console.log('FieldsBuilder - Time range case reached for field:', fields.name);
      // Handle both original field name and modified field name (with date and ID suffix)
      let fieldRange: any = range[fields.name as any];
      
      // If not found with the current name, try to find it with the original name pattern
      if (!fieldRange && fields.name && fields.name.includes('-')) {
        // Extract the original field name (before the first dash)
        const originalFieldName = fields.name.split('-')[0];
        fieldRange = range[originalFieldName as any];
      }
      
      // Add a state to detect mobile view
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [isMobile, setIsMobile] = useState(false);

      // eslint-disable-next-line react-hooks/rules-of-hooks
      useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 600);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
      }, []);

      if (isMobile) {
        // Mobile UI: stack vertically
        return (
          <>
            {fields.hideLabel && <KenTextLabel label={fields.label} labelClassName={classes.inputLableCustom} />}
            <Box component={'div'} mt={0.5} display="flex" flexDirection="column" gap={2}>
              {/* Start Time Input */}
              <Controller
                control={control}
                name={`${fields.name}.startTime`}
                rules={{
                  required: 'Start time is required',
                  validate: (value) => value !== '' || 'Start time cannot be empty',
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <KenTextField
                    variant="outlined"
                    type="time"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '45px',
                        width: '100%',
                        '& fieldset': {
                          border: '1px solid grey',
                          borderRadius: '8px',
                        },
                      },
                    }}
                    value={value || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newStartTime = e.target.value;
                      let updatedEndTime = fieldRange?.endTime || '';
                      if (updatedEndTime && updatedEndTime <= newStartTime) {
                        updatedEndTime = '';
                      }
                      
                      // Debug logging
                      console.log('FieldsBuilder - Start time changed:', {
                        fieldName: fields.name,
                        newStartTime,
                        updatedEndTime,
                        fieldRange
                      });
                      
                      // Update both the current field name and the original field name in the context
                      dispatch({
                        type: 'updateTimeRange',
                        value: {
                          fieldName: fields.name,
                          fileData: { startTime: newStartTime, endTime: updatedEndTime },
                        },
                      });
                      
                      // Also update the original field name if it's different
                      if (fields.name && fields.name.includes('-')) {
                        const originalFieldName = fields.name.split('-')[0];
                        if (originalFieldName !== fields.name) {
                          console.log('FieldsBuilder - Also updating original field name:', originalFieldName);
                          dispatch({
                            type: 'updateTimeRange',
                            value: {
                              fieldName: originalFieldName,
                              fileData: { startTime: newStartTime, endTime: updatedEndTime },
                            },
                          });
                        }
                      }
                      
                      onChange(e);
                    }}
                    error={!!error}
                    helperText={error ? error.message : ''}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography>Start time</Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* End Time Input */}
              <Controller
                control={control}
                name={`${fields.name}.endTime`}
                rules={{
                  required: 'End time is required',
                  validate: (value) => value !== '' || 'End time cannot be empty',
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <KenTextField
                    disabled={!startTimeValue}
                    variant="outlined"
                    type="time"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '45px',
                        width: '100%',
                        '& fieldset': {
                          border: '1px solid grey',
                          borderRadius: '8px',
                        },
                      },
                    }}
                    value={value || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newEndTime = e.target.value;
                      if (startTimeValue && newEndTime > startTimeValue) {
                        // Debug logging
                        console.log('FieldsBuilder - End time changed (mobile):', {
                          fieldName: fields.name,
                          startTimeValue,
                          newEndTime
                        });
                        
                        // Update both the current field name and the original field name in the context
                        dispatch({
                          type: 'updateTimeRange',
                          value: {
                            fieldName: fields.name,
                            fileData: { startTime: startTimeValue, endTime: newEndTime },
                          },
                        });
                        
                        // Also update the original field name if it's different
                        if (fields.name && fields.name.includes('-')) {
                          const originalFieldName = fields.name.split('-')[0];
                          if (originalFieldName !== fields.name) {
                            console.log('FieldsBuilder - Also updating original field name (mobile):', originalFieldName);
                            dispatch({
                              type: 'updateTimeRange',
                              value: {
                                fieldName: originalFieldName,
                                fileData: { startTime: startTimeValue, endTime: newEndTime },
                              },
                            });
                          }
                        }
                        
                        onChange(e);
                      }
                    }}
                    error={!!error}
                    helperText={error ? error.message : ''}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography>End time</Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </>
        );
      }

      // Desktop UI: side by side
      return (
        <>
          {fields.hideLabel && <KenTextLabel label={fields.label} labelClassName={classes.inputLableCustom} />}
          <Box component={'div'} mt={0.5} className="d-flex align-items-center">
            {/* Start Time Input */}
            <Controller
              control={control}
              name={`${fields.name}.startTime`}
              rules={{
                required: 'Start time is required',
                validate: (value) => value !== '' || 'Start time cannot be empty',
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <KenTextField
                  variant="outlined"
                  type="time"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '60px',
                      width: '13vw',
                      '@media (max-width: 1440px)': {
                        height: '45px',
                        width: '13vw',
                      },
                      '& fieldset': {
                        border: '1px solid grey',
                        borderRadius: '8px 0px 0px 8px',
                        borderRight: 'none',
                      },
                    },
                  }}
                  value={value || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newStartTime = e.target.value;
                    let updatedEndTime = fieldRange?.endTime || '';

                    // If the end time is before the new start time, reset it
                    if (updatedEndTime && updatedEndTime <= newStartTime) {
                      updatedEndTime = '';
                    }

                    // Debug logging
                    console.log('FieldsBuilder - Start time changed (desktop):', {
                      fieldName: fields.name,
                      newStartTime,
                      updatedEndTime,
                      fieldRange
                    });

                    // Update both the local context and the form state
                    dispatch({
                      type: 'updateTimeRange',
                      value: {
                        fieldName: fields.name,
                        fileData: { startTime: newStartTime, endTime: updatedEndTime },
                      },
                    });
                    
                    // Also update the original field name if it's different
                    if (fields.name && fields.name.includes('-')) {
                      const originalFieldName = fields.name.split('-')[0];
                      if (originalFieldName !== fields.name) {
                        console.log('FieldsBuilder - Also updating original field name (desktop):', originalFieldName);
                        dispatch({
                          type: 'updateTimeRange',
                          value: {
                            fieldName: originalFieldName,
                            fileData: { startTime: newStartTime, endTime: updatedEndTime },
                          },
                        });
                      }
                    }
                    
                    // Update the form state
                    onChange(e);
                    
                    // Also update the end time field in the form if it needs to be reset
                    if (updatedEndTime === '') {
                      setValue(`${fields.name}.endTime`, '');
                    }
                  }}
                  error={!!error}
                  helperText={error ? error.message : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography>Start time</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            {/* End Time Input */}
            <Controller
              control={control}
              name={`${fields.name}.endTime`}
              rules={{
                required: 'End time is required',
                validate: (value) => value !== '' || 'End time cannot be empty',
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <KenTextField
                  disabled={!startTimeValue}
                  variant="outlined"
                  type="time"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '60px',
                      width: '13vw',
                      '@media (max-width: 1440px)': {
                        height: '45px',
                        width: '13vw',
                      },
                      '& fieldset': {
                        border: '1px solid grey',
                        borderRadius: '0px 8px 8px 0px',
                        borderLeft: 'none',
                      },
                    },
                  }}
                  value={value || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newEndTime = e.target.value;
                    if (startTimeValue && newEndTime > startTimeValue) {
                      // Debug logging
                      console.log('FieldsBuilder - End time changed (desktop):', {
                        fieldName: fields.name,
                        startTimeValue,
                        newEndTime
                      });
                      
                      // Update both the current field name and the original field name in the context
                      dispatch({
                        type: 'updateTimeRange',
                        value: {
                          fieldName: fields.name,
                          fileData: { startTime: startTimeValue, endTime: newEndTime },
                        },
                      });
                      
                      // Also update the original field name if it's different
                      if (fields.name && fields.name.includes('-')) {
                        const originalFieldName = fields.name.split('-')[0];
                        if (originalFieldName !== fields.name) {
                          console.log('FieldsBuilder - Also updating original field name (desktop):', originalFieldName);
                          dispatch({
                            type: 'updateTimeRange',
                            value: {
                              fieldName: originalFieldName,
                              fileData: { startTime: startTimeValue, endTime: newEndTime },
                            },
                          });
                        }
                      }
                      
                      onChange(e);
                    }
                  }}
                  error={!!error}
                  helperText={error ? error.message : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography>End time</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </>
      );
    case 'date':
      return <CustomDateField fields={{ ...fields }} />;
    case 'autoComplete':
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [apiOptions, setApiOptions] = useState([]);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [eventtimezone, setEventtimezone] = useState([]);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [languageofdelivery, setLanguageofdelivery] = useState([]);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [targetAudience, settargetAudience] = useState([]);
      // Default values for multi-selects
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [defaultTimezone, setDefaultTimezone] = useState<any[]>([]);
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [defaultTargetAudience, setDefaultTargetAudience] = useState<any[]>([]);
      // Add state for defaultLanguageOfDelivery
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [defaultLanguageOfDelivery, setDefaultLanguageOfDelivery] = useState<any[]>([]);
      console.log(apiOptions, 'apiOptionsapiOptions');

      // eslint-disable-next-line react-hooks/rules-of-hooks
      useEffect(() => {
        if (fields.label === 'Event Category' || fields.label === 'Event timezone' || fields.label === 'Language of delivery' || fields.label === 'Suitable for') {
          getCategoryPicklistValue()
            .then((data:any) => {
              const newOptions = data.result.data.values.map((item: any) => ({
                label: item.label,
                value: item.value,
              }));
              const newOptionsEventtimezone = data.result.data.eventTimezone.map((item: any) => ({
                label: item.label,
                value: item.value,
              }));
              const newLanguageofdelivery = data.result.data.languages.map((item: any) => ({
                label: item.label,
                value: item.value,
              }));
              const targetAudiences = data.result.data.targetAudience.map((item: any) => ({
                label: item.label,
                value: item.value,
              }));

              setApiOptions(newOptions);
              setEventtimezone(newOptionsEventtimezone)
              setLanguageofdelivery(newLanguageofdelivery)
              settargetAudience(targetAudiences)

              // Set default timezone based on user's local timezone
              if (fields.label === 'Event timezone') {
                // Try to match user's local timezone with the API options
                const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                // Find the option that includes the user's timezone string
                const matched = newOptionsEventtimezone.find(
                  (opt: any) =>
                    opt.label.includes(userTimeZone) ||
                    opt.value.includes(userTimeZone)
                );
                if (matched) {
                  setDefaultTimezone([matched]);
                  setProcessedValue([matched]);
                } else {
                  // fallback: if India Standard Time exists, set as default
                  const indiaTz = newOptionsEventtimezone.find(
                    (opt: any) =>
                      opt.label.includes('India Standard Time') ||
                      opt.value.includes('India Standard Time')
                  );
                  if (indiaTz) {
                    setDefaultTimezone([indiaTz]);
                    setProcessedValue([indiaTz]);
                  }
                }
              }

              // Set default for Suitable for: Alumni
              if (fields.label === 'Suitable for') {
                const alumni = targetAudiences.find(
                  (opt: any) => opt.value === 'Alumni'
                );
                if (alumni) {
                  setDefaultTargetAudience([alumni]);
                  setProcessedValue([alumni]);
                }
              }

              // Set default for Language of delivery
              if (fields.label === 'Language of delivery') {
                // Try to find "English" (case-insensitive)
                const englishOption = newLanguageofdelivery.find(
                  (opt: any) =>
                    typeof opt.label === 'string' &&
                    opt.label.trim().toLowerCase() === 'english'
                );
                if (englishOption) {
                  setDefaultLanguageOfDelivery([englishOption]);
                  setProcessedValue([englishOption]);
                } else if (newLanguageofdelivery.length === 1) {
                  setDefaultLanguageOfDelivery([newLanguageofdelivery[0]]);
                  setProcessedValue([newLanguageofdelivery[0]]);
                }
              }
            })
            .catch((error:any) => console.error('Error fetching picklist values:', error));
        }
      }, [fields.label]);

      return (
        <Controller
          control={control}
          defaultValue={
            fields.label === 'Event timezone'
              ? defaultTimezone
              : fields.label === 'Suitable for'
                ? defaultTargetAudience
                : fields.label === 'Language of delivery'
                  ? defaultLanguageOfDelivery
                  : []
          }
          render={({ field: { value, onChange } }) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useEffect(() => {
              if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
                const transformed = value[0].split(';').map((lang) => ({
                  label: lang.trim(),
                  value: lang.trim(),
                }));
                setProcessedValue(transformed);
              }
              // Set processedValue for default selections
              if (
                (fields.label === 'Event timezone' && defaultTimezone.length > 0 && (!value || value.length === 0)) ||
                (fields.label === 'Suitable for' && defaultTargetAudience.length > 0 && (!value || value.length === 0)) ||
                (fields.label === 'Language of delivery' && defaultLanguageOfDelivery.length > 0 && (!value || value.length === 0))
              ) {
                setProcessedValue(
                  fields.label === 'Event timezone'
                    ? defaultTimezone
                    : fields.label === 'Suitable for'
                      ? defaultTargetAudience
                      : fields.label === 'Language of delivery'
                        ? defaultLanguageOfDelivery
                        : []
                );
                onChange(
                  fields.label === 'Event timezone'
                    ? defaultTimezone
                    : fields.label === 'Suitable for'
                      ? defaultTargetAudience
                      : fields.label === 'Language of delivery'
                        ? defaultLanguageOfDelivery
                        : []
                );
              }
            }, [value, defaultTimezone, defaultTargetAudience, defaultLanguageOfDelivery, fields.label, onChange]);

            return (
              <>
                {fields.hideLabel && <KenTextLabel label={fields.label} labelClassName={classes.inputLableCustom} />}
                <Autocomplete
                  multiple
                  id="tags-outlined"
                  options={
                    fields.label === 'Event Category'? apiOptions

                      : fields.label === 'Event timezone' ? eventtimezone 

                      : fields.label === 'Language of delivery' ? languageofdelivery

                      : fields.label === 'Suitable for' ? targetAudience
                      : fields.options 
                        || []
                  }
                  getOptionLabel={(option) => option?.label || ''}
                  value={processedValue}
                  onChange={(_, newValue) => {
                    if (newValue.length > 9) {
                      enqueueSnackbar('You can only select up to 9 values.', {
                        variant: 'error',
                        autoHideDuration: 1000,
                      });
                      return;
                    }
                    setProcessedValue(newValue);
                    onChange(newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option.label === value.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={fields.placeholder}
                      placeholder={processedValue?.length === fields.options?.length ? '' : fields.placeholder}
                      sx={{
                        margin: '6px 0px',
                        '& .MuiOutlinedInput-root': {
                          height: 'auto',
                          minHeight: '60px',
                          maxHeight: '140px',
                          overflowY: 'auto',
                          borderRadius: '6px',
                          fontSize: '14px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          '@media (max-width: 1440px)': {
                            minHeight: '45px',
                            maxHeight: '170px',
                            fontSize: '12px',
                            paddingRight: "0px !important"
                          },
                        },
                        '& .MuiInputBase-input': {
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '1px',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          fontSize: '14px',
                          '@media (max-width: 1440px)': {
                            fontSize: '12px',
                          },
                        },
                      }}
                    />
                  )}
                />
              </>
            );
          }}
          {...register(fields.name ?? 'defaultName', fields.validation)}
        />
      );
    case 'radio':
      const customOptions = fields.radioOptions;
      return (
        <div style={{ ...fields.fieldStyle }}>
          <Controller
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <>
                  {fields.hideLabel && <KenTextLabel label={fields.label} labelClassName={classes.inputLableCustom} />}
                  <RadioGroup onChange={onChange} value={value} {...fields.radioGroupProps}>
                    {customOptions?.map((items, index) => {
                      return (
                        <FormControlLabel
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '1rem',
                              '@media (max-width: 1440px)': {
                                fontSize: '0.9rem',
                              },
                            },
                          }}
                          value={items.value}
                          control={<Radio />}
                          label={items.label}
                          key={index}
                        />
                      );
                    })}
                  </RadioGroup>
                </>
              );
            }}
            {...register(`${fields.name}`, { ...fields?.validation })}
          />
        </div>
      );
    case 'textarea':
      return (
        <div style={{ ...fields.fieldStyle }}>
          <Controller
            control={control}
            rules={{
              ...fields?.validation,
              minLength: fields?.validation?.minLength || { value: 1, message: 'Minimum 1 characters required' },
              maxLength: fields?.validation?.maxLength || { value: 1000, message: 'Maximum 1000 characters allowed' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              // Type guards for min/max
              const minRule = fields?.validation?.minLength;
              const maxRule = fields?.validation?.maxLength;
              const min =
                typeof minRule === 'object' && minRule !== null && 'value' in minRule
                  ? minRule.value
                  : typeof minRule === 'number'
                  ? minRule
                  : 10;
              const max =
                typeof maxRule === 'object' && maxRule !== null && 'value' in maxRule
                  ? maxRule.value
                  : typeof maxRule === 'number'
                  ? maxRule
                  : 1000;
              // Type guard for showCharCount
              const showCharCount = (fields as any).showCharCount === true;
              const currentLength = value ? value.length : 0;
              return (
                <>
                  <KenInput
                    onChange={onChange}
                    value={value}
                    error={error?.message}
                    errorMessage={error?.message}
                    border
                    customMargin
                    labelClassName={classes.inputLableCustom}
                    InputProps={{
                      className: classes.inputStyles,
                      inputProps: { minLength: min, maxLength: max },
                    }}
                    {...fields}
                  />
                  {showCharCount && (
                    <div
                      style={{
                        textAlign: 'right',
                        fontSize: 12,
                        color: currentLength >= max ? 'red' : '#888',
                        marginTop: 2,
                        fontWeight: currentLength >= max ? 600 : 400,
                      }}
                    >
                      {currentLength}/{max}
                    </div>
                  )}
                </>
              );
            }}
            {...register(`${fields.name}`, { ...fields?.validation })}
          />
        </div>
      );
    case 'select':
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [Timezone, setTimezone] = useState<any>([]);
      console.log(Timezone, 'fields.label');

      // eslint-disable-next-line react-hooks/rules-of-hooks
      useEffect(() => {
        
        getEventsData()
          .then(async (res:any  ) => {
            if (res.status === 'true' && res?.result && typeof res.result === 'string') {
              try {
                const eventData = JSON.parse(res.result)?.find((items: any) => {
                  return items.Id === eventId;
                });
              const suitableForOptions = eventData?.timezone
                ?.split(';')
                .map((item: string) => ({
                  label: item.trim(),
                  value: item.trim(),
                }));

              setTimezone(suitableForOptions); // or setSuitableFor(suitableForOptions) if that's the right state
            } catch (error) {
              console.error('Error parsing form builder event data:', error);
              setTimezone([]);
            }
          } else {
            setTimezone([]);
          }
        })
      }, [eventId]);
      return (
        <div style={{ ...fields.fieldStyle }}>
          <Controller
            control={control}
            render={({ field: { onChange, value}, fieldState: { error } }) => {
              const customOptions = fields.optionsMeta && Object.keys(options).length > 0 ? options[fields.optionsMeta?.fieldName] : fields.options || [];
              const getOptionObj = customOptions.find((item: any) => item.value === value || item.label === value) || null;

                // Determine the default value for timezone select
                let selectValue = value;
                if (fields?.label === 'Section timezone' && Array.isArray(Timezone)) {
                if (!value) {
                  if (Timezone.length === 1) {
                  selectValue = Timezone[0]?.value;
                  onChange(Timezone[0]?.value);
                  } else if (Timezone.length > 1) {
                  // Get user's timezone offset in minutes and convert to "+05:30" format
                  const offsetMinutes = -new Date().getTimezoneOffset();
                  const sign = offsetMinutes >= 0 ? '+' : '-';
                  const absMinutes = Math.abs(offsetMinutes);
                  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
                  const minutes = String(absMinutes % 60).padStart(2, '0');
                  const gmtString = `GMT${sign}${hours}:${minutes}`;
                  // Try to match the label that starts with the formatted GMT string
                  const matched = Timezone.find(
                    (opt: any) =>
                    (opt.label && opt.label.startsWith(gmtString)) ||
                    (opt.value && opt.value.startsWith(gmtString))
                  );
                  if (matched) {
                    selectValue = matched.value;
                    onChange(matched.value);
                  }
                  }
                }
                }

                return fields.withSearch === true ? (
                <>
                  {fields.hideLabel && <KenTextLabel label={fields.label} labelClassName={classes.inputLableCustom} />}
                  <Autocomplete
                  options={fields?.label === 'Section timezone' ? Timezone : customOptions}
                  getOptionLabel={(option: any) => option?.label || ''}
                  filterSelectedOptions
                  freeSolo
                  value={
                    fields?.label === 'Section timezone'
                      ? (Timezone.find((item: any) => item.value === selectValue) || null)
                      : (customOptions.find((item: any) => item.value === selectValue) || null)
                  }
                  isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                  onInputChange={(_, v) => {
                    const selectedOption = customOptions.find((option: any) => option.label === v);
                    if (selectedOption) {
                    onChange(selectedOption.value);
                    } else {
                    onChange(v);
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (!newValue) {
                    onChange('');
                    } else if (typeof newValue === 'string') {
                    onChange(newValue);
                    } else {
                    onChange(newValue.value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                    error={typeof error?.message === 'string'}
                    helperText={error?.message}
                    sx={{
                      margin: '6px 0px',
                      '& .MuiOutlinedInput-root': {
                      height: '60px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      '@media (max-width: 1440px)': {
                        height: '45px',
                        fontSize: '12px',
                      },
                      },
                      '& .MuiInputBase-input::placeholder': {
                      '@media (max-width: 1440px)': {
                        fontSize: '12px',
                      },
                      fontSize: '14px',
                      },
                    }}
                    {...params}
                    placeholder={fields.placeholder}
                    />
                  )}
                  />
                </>
                ) : (
                <KenSelect
                  onChange={onChange}
                  value={
                    value !== undefined && value !== null && value !== ''
                      ? value
                      : (Timezone.find((item: any) => item.value === selectValue)?.value ||
                         '')
                  }
                  error={error?.message}
                  border
                  customMargin
                  labelClassName={classes.inputLableCustom}
                  InputProps={{ className: classes.inputStyles }}
                  {...fields}
                  options={fields?.label === 'Section timezone' ? Timezone : customOptions}
                />
                );
            }}
            {...register(`${fields.name}`, { ...fields?.validation })}
          />
        </div>
      );
    case 'none':
      return <div />;
    case 'text':
      return (
        <div style={{ ...fields.fieldStyle }}>
          <Controller
            control={control}
            rules={{
              ...fields?.validation,
              minLength: fields?.validation?.minLength || { value: 1, message: 'Minimum 1 characters required' },
              maxLength: fields?.validation?.maxLength || { value: 100, message: 'Maximum 100 characters allowed' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              // Type guards for min/max
              const minRule = fields?.validation?.minLength;
              const maxRule = fields?.validation?.maxLength;
              const min =
                typeof minRule === 'object' && minRule !== null && 'value' in minRule
                  ? minRule.value
                  : typeof minRule === 'number'
                  ? minRule
                  : 1;
              const max =
                typeof maxRule === 'object' && maxRule !== null && 'value' in maxRule
                  ? maxRule.value
                  : typeof maxRule === 'number'
                  ? maxRule
                  : 100;
              return (
                <KenInput
                  border
                  customMargin
                  labelClassName={classes.inputLableCustom}
                  onChange={onChange}
                  value={value}
                  error={error?.message}
                  errorMessage={error?.message}
                  InputProps={{
                    className: classes.inputStyles,
                    inputProps: {
                      minLength: min,
                      maxLength: max,
                    },
                  }}
                  {...fields}
                />
              );
            }}
            {...register(`${fields.name}`, { ...fields?.validation })}
          />
        </div>
      );
    default:
      return (
        <div style={{ ...fields.fieldStyle }}>
          <Controller
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <KenInput
                  border
                  customMargin
                  labelClassName={classes.inputLableCustom}
                  onChange={(e: any) => {
                    onChange(e);
                    console.log(`from form value ${fields.name}`, e.target.value);
                  }}
                  value={value}
                  error={error?.message}
                  errorMessage={error?.message}
                  InputProps={{ className: classes.inputStyles }}
                  {...fields}
                />
              );
            }}
            {...register(`${fields.name}`, { ...fields?.validation })}
          />
        </div>
      );
  }
};

export default FieldsBuilder;
